import React, { useState, useEffect } from 'react';
import { adminAPI, useAdminAuth } from '@/contexts/AdminAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useModal } from '@/contexts/ModalContext';
import { toast } from '@/components/ui/sonner';
import {
  Users,
  Search,
  Filter,
  MoreHorizontal,
  UserCheck,
  UserX,
  Trash2,
  Shield,
  Eye,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Clock,
  UserPlus
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UserDetailsModal } from '@/components/admin/UserDetailsModal';

interface User {
  id: string; // MongoDB _id returned as 'id'
  uid: string; // This is the firebaseUid from backend
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  isBlocked: boolean;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string;
  usage: {
    leadsScraped?: number;
    emailsSent?: number;
    filesUploaded?: number;
    aiChats?: number;
    lastActivity?: string;
  };
  subscription: {
    plan?: string;
    status?: string;
  };
}

interface UsersResponse {
  users: User[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalUsers: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

const AdminUsersPage: React.FC = () => {
  const { user, isRootAdmin } = useAdminAuth();
  const { showConfirmModal } = useModal();
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);



  // Filters
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  useEffect(() => {
    loadUsers();
  }, [filters]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminAPI.getUsers(filters);
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error: any) {
      setError(error.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleViewUserDetails = (user: User) => {
    setSelectedUserId(user.uid);
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const handleUserAction = async (event: React.MouseEvent, action: string, targetUser: User, data?: any) => {
    // Prevent event bubbling
    event.stopPropagation();
    event.preventDefault();

    console.log(`Performing ${action} on user: ${targetUser.email}`);

    const userId = targetUser.uid;

    if (!userId) {
      console.error('No user ID found:', targetUser);
      setError('User ID not found');
      return;
    }

    // Get confirmation for destructive actions
    let confirmed = true;

    if (action === 'delete') {
      confirmed = await showConfirmModal({
        title: 'Delete User',
        description: 'Are you sure you want to permanently delete this account?',
        variant: 'destructive',
        confirmText: 'Confirm',
        cancelText: 'Cancel'
      });
    } else if (action === 'block') {
      confirmed = await showConfirmModal({
        title: 'Block User',
        description: 'Do you want to block this user?',
        variant: 'warning',
        confirmText: 'Block',
        cancelText: 'Cancel'
      });
    } else if (action === 'unblock') {
      confirmed = await showConfirmModal({
        title: 'Unblock User',
        description: 'Unblock this user?',
        variant: 'default',
        confirmText: 'Unblock',
        cancelText: 'Cancel'
      });
    } else if (action === 'promote') {
      confirmed = await showConfirmModal({
        title: 'Promote to Admin',
        description: 'Are you sure you want to promote this user to admin?',
        variant: 'default',
        confirmText: 'Confirm',
        cancelText: 'Cancel'
      });
    } else if (action === 'demote') {
      confirmed = await showConfirmModal({
        title: 'Demote Admin',
        description: 'This will remove admin privileges. Proceed?',
        variant: 'warning',
        confirmText: 'Confirm',
        cancelText: 'Cancel'
      });
    }

    if (!confirmed) {
      return;
    }

    try {
      setActionLoading(`${action}-${userId}`);
      setError(''); // Clear any previous errors

      console.log(`Performing ${action} on user:`, userId, targetUser.email);

      switch (action) {
        case 'block':
          await adminAPI.toggleUserBlock(userId, true, data?.reason);
          break;
        case 'unblock':
          await adminAPI.toggleUserBlock(userId, false);
          break;
        case 'delete':
          await adminAPI.deleteUser(userId, data?.reason);
          break;
        case 'promote':
          if (!targetUser?.email) {
            throw new Error('User email not found');
          }
          await adminAPI.promoteToAdmin(targetUser.email);
          break;
        case 'demote':
          if (!targetUser?.email) {
            throw new Error('User email not found');
          }
          await adminAPI.demoteAdmin(targetUser.email);
          break;
      }

      // Reload users to reflect changes
      await loadUsers();

      // Show success toast
      const actionMessages = {
        delete: `User ${targetUser.email} has been deleted successfully`,
        block: `User ${targetUser.email} has been blocked`,
        unblock: `User ${targetUser.email} has been unblocked`,
        promote: `User ${targetUser.email} has been promoted to admin`,
        demote: `User ${targetUser.email} has been demoted from admin`
      };

      toast.success(actionMessages[action as keyof typeof actionMessages] || `${action} completed successfully`);
      console.log(`${action} completed successfully`);
    } catch (error: any) {
      console.error(`Error performing ${action}:`, error);
      const errorMessage = error.message || `Failed to ${action} user`;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const getUserStatusBadge = (user: User) => {
    if (user.isBlocked) {
      return <Badge variant="destructive" className="flex items-center gap-1">
        <UserX className="h-3 w-3" />
        Blocked
      </Badge>;
    }
    if (!user.isActive) {
      return <Badge variant="secondary" className="flex items-center gap-1">
        <Clock className="h-3 w-3" />
        Inactive
      </Badge>;
    }
    return <Badge variant="default" className="flex items-center gap-1 bg-green-100 text-green-800">
      <CheckCircle className="h-3 w-3" />
      Active
    </Badge>;
  };

  const getRoleBadge = (role: string) => {
    const roleColors = {
      'root_admin': 'bg-purple-100 text-purple-800',
      'admin': 'bg-blue-100 text-blue-800',
      'user': 'bg-gray-100 text-gray-800'
    };
    
    return (
      <Badge className={`${roleColors[role as keyof typeof roleColors] || roleColors.user}`}>
        {role === 'root_admin' ? 'Root Admin' : role.charAt(0).toUpperCase() + role.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && users.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">User Management</h1>
        </div>
        <div className="grid grid-cols-1 gap-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" />
            User Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage user accounts, permissions, and access
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-red-800">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Search Users
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Email or name..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Role
                </label>
                <select
                  value={filters.role}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Roles</option>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="root_admin">Root Admin</option>
                </select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <Button type="submit" className="w-full">
                  <Filter className="h-4 w-4 mr-2" />
                  Apply Filters
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Users ({pagination?.totalUsers || 0})</span>
            <div className="text-sm text-muted-foreground">
              Page {pagination?.currentPage || 1} of {pagination?.totalPages || 1}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">User</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Role</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Usage</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Joined</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => {
                  // Create handlers for this specific user to ensure proper closure
                  const handleUserActionForThisUser = (event: React.MouseEvent, action: string, data?: any) => {
                    handleUserAction(event, action, user, data);
                  };

                  return (
                    <tr key={user.id || user.uid || user.email} className="border-b border-border hover:bg-accent/5" data-user-index={index}>
                    <td className="py-4 px-4">
                      <div>
                        <div className="font-medium text-foreground">
                          {user.displayName || user.email.split('@')[0]}
                        </div>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="py-4 px-4">
                      {getUserStatusBadge(user)}
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-sm space-y-1">
                        <div>Leads: {user.usage.leadsScraped}</div>
                        <div>Emails: {user.usage.emailsSent}</div>
                        <div>Files: {user.usage.filesUploaded}</div>
                        <div>Chats: {user.usage.aiChats}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-sm text-muted-foreground">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewUserDetails(user)}
                              className="hover:bg-blue-50 hover:border-blue-300"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>View user details and activity</p>
                          </TooltipContent>
                        </Tooltip>

                        {user.role !== 'root_admin' && (
                          <>
                            {user.isBlocked ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => handleUserActionForThisUser(e, 'unblock')}
                                    disabled={actionLoading === `unblock-${user.uid}`}
                                    className="hover:bg-green-50 hover:border-green-300"
                                  >
                                    <UserCheck className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Unblock user - Restore access to the platform</p>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => handleUserActionForThisUser(e, 'block', { reason: 'Admin action' })}
                                    disabled={actionLoading === `block-${user.uid}`}
                                    className="hover:bg-orange-50 hover:border-orange-300"
                                  >
                                    <UserX className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Block user - Suspend access to the platform</p>
                                </TooltipContent>
                              </Tooltip>
                            )}

                            {isRootAdmin && (
                              <>

                                {user.role === 'user' ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => handleUserActionForThisUser(e, 'promote')}
                                        disabled={actionLoading === `promote-${user.uid}`}
                                        className="hover:bg-purple-50 hover:border-purple-300"
                                      >
                                        <Shield className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Promote to Admin - Grant administrative privileges</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : user.role === 'admin' ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => handleUserActionForThisUser(e, 'demote')}
                                        disabled={actionLoading === `demote-${user.uid}`}
                                        className="hover:bg-yellow-50 hover:border-yellow-300"
                                      >
                                        <UserPlus className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Demote to User - Remove administrative privileges</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : null}

                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={(e) => handleUserActionForThisUser(e, 'delete', { reason: 'Admin deletion' })}
                                      disabled={actionLoading === `delete-${user.uid}`}
                                      className="hover:bg-red-100 hover:border-red-400"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Delete user - Permanently remove user and all data</p>
                                  </TooltipContent>
                                </Tooltip>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            
            {users.length === 0 && !loading && (
              <div className="text-center py-8 text-muted-foreground">
                No users found matching your criteria
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((pagination.currentPage - 1) * filters.limit) + 1} to{' '}
            {Math.min(pagination.currentPage * filters.limit, pagination.totalUsers)} of{' '}
            {pagination.totalUsers} users
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            
            <span className="text-sm text-muted-foreground">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
      </div>

      {/* User Details Modal */}
      {selectedUserId && (
        <UserDetailsModal
          open={showUserDetails}
          onOpenChange={setShowUserDetails}
          userId={selectedUserId}
          userEmail={selectedUser?.email}
        />
      )}
    </TooltipProvider>
  );
};

export default AdminUsersPage;
